import React, { useEffect, useState } from "react";
import {
  Calendar,
  Checkbox,
  Input,
  MultiSelect,
  LocationPicker,
} from "../../../../components/Shared/Forms";
import styles from "../duplicate-site.module.css";
import useApiService from "../../../../hooks/useApiService";
import { Industry } from "../../../../models/site-setup.interface";
import { formDataType } from "../../Create/AddSite";

interface SiteDetailsProps {
  formData: formDataType;
  setFormData: React.Dispatch<React.SetStateAction<formDataType>>;
  handleInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fieldErrors: { [key: string]: string };
  setFieldErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
}

function SiteDetails({
  formData,
  setFormData,
  handleInputChange,
  fieldErrors,
  setFieldErrors,
}: SiteDetailsProps) {
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [allOption, setAllOption] = useState<Industry>();
  const [gps, setGps] = useState<string>("");
  const [address, setAddress] = useState(formData.address || "");

  const { loading: loadingIndustries } = useApiService<Industry[]>({
    url: "/industry",
    method: "GET",
    apiName: "core",
    onSuccess: data => {
      setIndustries(data);
      const allInd = data.find(ind => ind.name.toLowerCase() === "all");
      if (allInd) setAllOption(allInd);
    },
    onError: error => {
      console.error(error);
    },
    fetchOnMount: true,
  });

  useEffect(() => {
    if (address && gps) {
      const gpsArr = gps.split(",");
      // Clear any existing error for this field
      if (fieldErrors[address]) {
        setFieldErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          delete newErrors[address];
          return newErrors;
        });
      }
      setFormData({
        ...formData,
        address: address,
        gps_latitude: parseFloat(gpsArr[0]),
        gps_longitude: parseFloat(gpsArr[1]),
      });
    }
  }, [address, gps]);

  function selectIndustry(item: { label: string; value: number }) {
    const industries = formData?.industry || [];

    // check if industryId contains any new ids that are not in industries
    const isNewIndustry = industries.includes(item.value);
    if (isNewIndustry) {
      // remove the industry id from industries
      const updatedIndustries = industries.filter(id => item.value !== id);
      setFormData({
        ...formData,
        industry: updatedIndustries,
      });
      return;
    }
    // check if industryId is the "all" option
    if (item.label === "All") {
      setFormData({
        ...formData,
        industry: [item.value],
      });
      return;
    }

    const isAllSelected = industries.includes(allOption?.id || -1);
    if (isAllSelected) {
      // remove the "all" option from industries
      const updatedIndustries = industries.filter(id => id !== allOption?.id);
      setFormData({
        ...formData,
        industry: [...updatedIndustries, item.value],
      });
      return;
    }

    setFormData({
      ...formData,
      industry: [...industries, ...[item.value]],
    });
  }

  // console.log("formData == ", formData);

  return (
    <div className={"my-4"}>
      <div className={styles["responsive-grid-2"]}>
        <Input
          label={"Site/Project name"}
          name={"name"}
          value={formData.name}
          onChange={handleInputChange}
          required={true}
          error={fieldErrors?.name}
        />

        <MultiSelect
          label={"Industry"}
          options={industries.map(industry => ({
            label: industry.name,
            value: industry.id,
          }))}
          loading={loadingIndustries}
          onChange={(val: any) => {
            selectIndustry(val.selectedOption as { label: string; value: number });
          }}
          value={formData.industry}
          required={true}
          error={fieldErrors?.industry}
          onSelectAll={(val: any) => {
            setFormData({
              ...formData,
              industry: val,
            });
          }}
        />
      </div>
      <div className={styles["responsive-grid-2"]}>
        <div className={styles["responsive-grid-2"]}>
          <Calendar
            label={"Expected start date"}
            name={"start_at"}
            value={formData.start_at}
            onChange={handleInputChange}
            error={fieldErrors?.start_at}
          />
          <div>
            <Calendar
              label={"Expected end date"}
              name={"end_at"}
              value={formData.end_at}
              onChange={handleInputChange}
              error={fieldErrors?.end_at}
            />
            <Checkbox
              checked={formData.end_at === ""}
              label={"End date not known"}
              onChange={e => {
                if (e.checked) {
                  setFormData({
                    ...formData,
                    end_at: "",
                  });
                } else if (formData.end_at === "") {
                  const oneMonthFromNow = new Date();
                  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

                  setFormData({
                    ...formData,
                    end_at: oneMonthFromNow,
                  });
                }
                console.log("e", e);
              }}
            />
          </div>
        </div>

        <LocationPicker
          label="Address"
          locationGps={gps}
          setLocationGps={setGps}
          locationLabel={address}
          required={true}
          setLocationLabel={setAddress}
          error={fieldErrors?.address}
        />
      </div>
    </div>
  );
}

export { SiteDetails };
